import type { McpAccessScope } from '../Api/McpAccess/McpAccessApi';
import { UserRole } from '../store/User';

export type McpEnvLabel = 'local' | 'dev' | 'qa' | 'prod' | 'unknown';

// Which roles get MCP access is also encoded by the settings route/nav gate (hasEngineerOrHigherPermissions
// in store/User.ts). Keep the two in lockstep: any role returning a non-empty list here must also pass that
// gate, or it would reach /settings and render an empty section (guarded today by the length === 0 early return).
// read_only is listed first so it is the least-privilege default when a role can pick more than one.
export const allowedMcpScopesForRole = (role?: UserRole): McpAccessScope[] => {
  if (role === UserRole.ENGINEER) return ['read_only', 'read_write'];
  if (role === UserRole.ADMIN) return ['read_only'];
  return [];
};

const parseHost = (raw: string): string => {
  try {
    return new URL(raw).hostname.toLowerCase();
  } catch {
    return '';
  }
};

export const deriveMcpEnvLabel = (apiServerUrl?: string): McpEnvLabel => {
  const raw = apiServerUrl ?? process.env.REACT_APP_API_SERVER_URL ?? '';
  const host = parseHost(raw);
  // A missing/unparseable server URL is a misconfiguration; surface it loudly rather than mislabelling as prod.
  if (!host) return 'unknown';
  if (host === 'localhost' || host === '127.0.0.1') return 'local';
  if (host.startsWith('dev.')) return 'dev';
  if (host.startsWith('qa.')) return 'qa';
  return 'prod';
};

export interface ClaudeMcpAddArgs {
  accessId: string;
  token: string;
  scope: McpAccessScope;
  apiServerUrl?: string;
}

export const buildClaudeMcpAddCommand = ({ accessId, token, scope, apiServerUrl }: ClaudeMcpAddArgs): string => {
  const baseUrl = (apiServerUrl ?? process.env.REACT_APP_API_SERVER_URL ?? '').replace(/\/+$/, '');
  const env = deriveMcpEnvLabel(baseUrl);
  const access = scope === 'read_write' ? 'write' : 'read';
  return (
    `claude mcp add --transport http tally-mcp-${access}-${env} ` +
    `${baseUrl}/api/mcp/${accessId} ` +
    `--header "Authorization: Bearer ${token}" -s user`
  );
};
